// Settings › Symbols › Upload tokens (D91 as amended, APS-456): the tokens a CI pipeline uploads
// this project's symbols with. Owners and admins create one in a dialog (a name, 7 to 365 days)
// and see it once, there, to copy; Apsio keeps its hash, never the token. Members see the list.
// A revoke is refused by the symbol upload API within 30 seconds.
import { useQuery } from '@tanstack/react-query'
import { useId, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import {
  orgQuery,
  uploadTokensQuery,
  useCreateUploadToken,
  useRevokeUploadToken,
} from '../api/queries'
import type { NewSymbolUploadToken } from '../api/types'
import { CodeBlock } from '../components/CodeBlock'
import { Confirm } from '../components/Confirm'
import { Btn, btnClass, EmptyNote, Section } from '../components/d'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { day } from '../lib/format'

const LIFETIMES = [7, 30, 90, 365] as const
/** The API's cap on a project's live upload tokens (@apsio/accounts MAX_LIVE_UPLOAD_TOKENS). */
const MAX_LIVE = 50
type Lifetime = (typeof LIFETIMES)[number]

export function UploadTokens({ orgId, projectId }: { orgId: string; projectId: string }) {
  const i18n = useI18n()
  const { t, rich, relative } = i18n
  const org = useQuery(orgQuery(orgId))
  const list = useQuery(uploadTokensQuery(orgId, projectId))
  const revoke = useRevokeUploadToken(orgId, projectId)
  const admin = org.data?.role === 'owner' || org.data?.role === 'admin'
  // "Sep 28" this year, with the year otherwise (the console's date rule).
  const short = (value: string) => day(i18n, value)
  const rows = list.data ?? []
  return (
    <Section
      title={t('symbols.docs.token')}
      meta={t('symbols.tokens.meta')}
      right={admin ? <CreateUploadToken orgId={orgId} projectId={projectId} /> : undefined}
    >
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorState error={list.error} retry={() => list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyNote title={t('symbols.tokens.none')}>{t('symbols.tokens.sub')}</EmptyNote>
      ) : (
        <ul className="rl ut-list" aria-label={t('symbols.tokens.list')}>
          {rows.map((row) => (
            <li key={row.id}>
              <code translate="no">{row.name}</code>
              {/* Shown under the row, the full width (order: 3): who, when, until when. The last use stays
                  the list's last child, its mono column on the right. */}
              <span className="se-hint ut-meta">
                {row.created_by_name
                  ? t('symbols.tokens.madeBy', {
                      name: row.created_by_name,
                      date: short(row.created_at),
                      expires: short(row.expires_at),
                    })
                  : t('symbols.tokens.made', {
                      date: short(row.created_at),
                      expires: short(row.expires_at),
                    })}
              </span>
              <span>
                {row.last_used_at
                  ? t('symbols.tokens.used', { when: relative(row.last_used_at) })
                  : t('symbols.tokens.unused')}
                {admin ? (
                  <>
                    {' '}
                    <Confirm
                      buttonClass={`${btnClass('ghost', 'sm')} danger ut-revoke`}
                      confirmClass="btn danger"
                      label={t('agents.tokens.revoke')}
                      buttonName={t('symbols.tokens.revokeNamed', { name: row.name })}
                      title={t('agents.tokens.revokeTitle')}
                      body={t('agents.tokens.revokeBody', { name: row.name })}
                      confirm={t('agents.tokens.revoke')}
                      busy={revoke.isPending}
                      onConfirm={() => revoke.mutate(row.id)}
                    />
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {admin ? null : <p className="se-hint">{t('symbols.tokens.membersSee')}</p>}
      <p className="se-hint" style={{ paddingTop: 10 }}>
        {rich('symbols.token')}
      </p>
      {revoke.error ? (
        <p role="alert" className="notice error">
          {t('state.failed')}
        </p>
      ) : null}
    </Section>
  )
}

/** Create an upload token, in a dialog that then shows it once. */
function CreateUploadToken({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t, date, rich } = useI18n()
  const create = useCreateUploadToken(orgId, projectId)
  const ref = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState('')
  const [days, setDays] = useState<Lifetime>(90)
  const [made, setMade] = useState<NewSymbolUploadToken>()
  const ids = { title: useId(), name: useId(), days: useId() }
  const ready = name.trim().length >= 1 && name.trim().length <= 100
  const close = () => {
    ref.current?.close()
    setMade(undefined)
  }
  return (
    <>
      <Btn
        variant="ghost"
        size="sm"
        onClick={() => {
          create.reset()
          setMade(undefined)
          ref.current?.showModal()
        }}
      >
        {t('symbols.tokens.create')}
      </Btn>
      <dialog ref={ref} aria-labelledby={ids.title} onClose={() => setMade(undefined)}>
        {made ? (
          <div className="stack" style={{ gap: 14 }}>
            <h2 id={ids.title} style={{ fontSize: 17 }}>
              {t('agents.tokens.theToken')}
            </h2>
            <p className="dim" role="status">
              {rich('symbols.tokens.madeOnce', {
                name: made.name,
                date: date(made.expires_at, { dateStyle: 'medium' }),
              })}
            </p>
            {/* The token wraps (break-all): all of it is visible before I copied it. */}
            <div style={{ minWidth: 0 }}>
              <CodeBlock label={t('agents.tokens.theToken')} lines={[made.token]} wrap />
            </div>
            <div className="head-actions">
              <button type="button" className="btn solid" onClick={close}>
                {t('agents.tokens.done')}
              </button>
            </div>
          </div>
        ) : (
          <form
            className="stack"
            style={{ gap: 14 }}
            aria-labelledby={ids.title}
            onSubmit={(e) => {
              e.preventDefault()
              if (!ready) return
              create.mutate(
                { name: name.trim(), days },
                {
                  onSuccess: (token) => {
                    setMade(token)
                    setName('')
                    setDays(90)
                  },
                },
              )
            }}
          >
            <h2 id={ids.title} style={{ fontSize: 17 }}>
              {t('symbols.tokens.create')}
            </h2>
            <p className="dim" style={{ fontSize: 13.5 }}>
              {t('symbols.tokens.sub')}
            </p>
            <div className="field">
              <label htmlFor={ids.name}>{t('agents.tokens.name')}</label>
              <input
                id={ids.name}
                className="input"
                maxLength={100}
                autoComplete="off"
                placeholder={t('symbols.tokens.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor={ids.days}>{t('agents.tokens.lifetime')}</label>
              <select
                id={ids.days}
                className="input"
                value={days}
                onChange={(e) => setDays(Number(e.target.value) as Lifetime)}
              >
                {LIFETIMES.map((d) => (
                  <option key={d} value={d}>
                    {t('agents.tokens.days', { days: d })}
                  </option>
                ))}
              </select>
            </div>
            {create.error ? (
              <p role="alert" className="notice error">
                {isApiError(create.error, 400)
                  ? t('agents.tokens.invalid')
                  : isApiError(create.error, 403)
                    ? t('agents.tokens.forbidden')
                    : isApiError(create.error, 422)
                      ? t('symbols.tokens.tooMany', { max: MAX_LIVE })
                      : t('state.failed')}
              </p>
            ) : null}
            <div className="head-actions">
              <button type="submit" className="btn solid" disabled={!ready || create.isPending}>
                {t('symbols.tokens.create')}
              </button>
              <button type="button" className="btn" onClick={close}>
                {t('settings.cancel')}
              </button>
            </div>
          </form>
        )}
      </dialog>
    </>
  )
}
